import { useEffect, useState } from 'react'
import Spinner from 'app/components/Spinner'
import { gqlQuery } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'
import { logWarn } from 'lib/logging'
import { graphql } from 'app/generated/gql'

let fetching: Record<string, Promise<string | undefined>> = {}
let cache: Record<string, string> = {}

const PMS_USER_NAME_QUERY = graphql(`
  query LazyPmsUserName($locationId: ID!, $userNum: Int!) {
    pmsUserName(locationId: $locationId, userNum: $userNum)
  }
`)

function cacheKey(locationId: string, userNum: number): string {
  return `${locationId}:${userNum}`
}

export async function getPmsUserName(locationId: string, userNum: number): Promise<string | undefined> {
  const key = cacheKey(locationId, userNum)
  if (cache[key] != null) return cache[key]
  if (fetching[key] != null) return fetching[key]
  if (!locationId || !userNum) return
  fetching[key] = (async () => {
    try {
      const { data, error } = await gqlQuery(PMS_USER_NAME_QUERY, { locationId, userNum })
      if (error) {
        logWarn(`pmsUserName(${locationId}, ${userNum}) returned an error`, { error })
        return undefined
      }
      // The resolver is nullable: a PMS user with no employee row has no name. That is expected, not a fault.
      if (!data?.pmsUserName) return undefined
      return (cache[key] = data.pmsUserName)
    } catch (err) {
      logWarn(`pmsUserName(${locationId}, ${userNum}) fetch threw`, err)
      return undefined
    }
  })()
  return fetching[key]
}

export function LazyPmsUserCell(props: {
  locationId: string
  userNum: number | undefined | null
  className?: string
  loadingIndicator?: boolean
}) {
  const key = props.locationId && props.userNum ? cacheKey(props.locationId, props.userNum) : ``
  const [name, setName] = useState<string>(key ? (cache[key] ?? ``) : ``)
  const [resolved, setResolved] = useState(Boolean(name))

  useEffect(() => {
    if (!props.locationId || !props.userNum) {
      setName(``)
      return
    }
    if (name && cache[key] === name) return
    else setName(``)
    setResolved(false)
    bg(
      getPmsUserName(props.locationId, props.userNum).then((fetched) => {
        if (fetched) setName(fetched)
        setResolved(true)
      }),
    )
  }, [key])

  if (!props.userNum) return null
  return name || resolved ? (
    <span className={props.className ?? 'text-body-xs'}>{name || `Staff`}</span>
  ) : props.loadingIndicator !== false ? (
    <Spinner size={5} />
  ) : null
}
